import classNames from 'classnames';
import * as React from 'react';
import { ButtonSize } from '../Button/ButtonSize.csharp';
import { ButtonTier } from '../Button/ButtonTier.csharp';
import { Link } from '../Link/Link';
import { LinkType } from '../Link/LinkType.csharp';
import { List } from '../List/List';
import { SvgIcon } from '../SvgIcon/SvgIcon';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import type { Theme } from '~/Utils/Theme';
import styles from './LinkBar.module.css';
import type { LinkBarColumnLayout } from './LinkBarColumnLayouts';
import type { LinkBarVariant } from './LinkBarVariants';
import type { LinkBarViewModel } from './LinkBarViewModel.csharp';

export interface LinkBarProps extends LinkBarViewModel {
    theme?: Theme;
    variant?: LinkBarVariant;
    columnLayout?: LinkBarColumnLayout;
}

export const LinkBar: React.FC<React.PropsWithChildren<LinkBarProps>> = props => {
    const contextualTheme = useTheme();
    const { links, variant = 'primary', theme = contextualTheme, columnLayout } = props;

    const getLinkVariant = () => {
        switch (variant) {
            case 'primary': {
                return { type: LinkType.Clear };
            }
            case 'secondary': {
                return {
                    type: LinkType.Button,
                    buttonTier: ButtonTier.Tertiary,
                    buttonSize: ButtonSize.ResponsiveFromSmallToMedium,
                };
            }
            default: {
                throw new Error(`Provided variant is not supported ${variant}`);
            }
        }
    };

    return (
        <>
            {links.length > 0 && (
                <div
                    className={classNames(
                        styles.LinkBar,
                        styles[`Variant--${variant}`],
                        columnLayout && styles[`Variant--${columnLayout}Columns`]
                    )}
                >
                    <div>
                        <List
                            className={styles.List}
                            items={links}
                            renderItem={(linkWithIcon, idx) => {
                                const { link, iconName } = linkWithIcon;

                                return (
                                    <li className={styles.LinkWrapper} key={`${link.href}__${idx}`}>
                                        <Link {...link} {...getLinkVariant()} className={styles.Link} theme={theme}>
                                            <div className={styles.LinkInner}>
                                                <SvgIcon iconName={iconName} className={styles.Icon} />
                                                <p className={styles.LinkLabel}>{link.label}</p>
                                            </div>
                                        </Link>
                                    </li>
                                );
                            }}
                        />
                    </div>
                </div>
            )}
        </>
    );
};
